import { __decorate } from "tslib";
import { Component, Prop, Mixins, Inject, Watch } from 'vue-property-decorator';
import { Mutation } from 'vuex-class';
import ProductGridView from '@/components/v2/ecommerce/ProductGridView.vue';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import ProductListView from '@/components/v2/ecommerce/ProductListView.vue';
import ViewRendererMixin from '@/helpers/vue_mixins/ViewRendererMixin';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import FbShopActivationForm from '@/components/v2/FbShopActivationForm.vue';
import VisibleProductsCount from '@/components/v2/VisibleProductsCount';
import AppBasicDetails from '@/components/v2/AppBasicDetails';
import AppBundle from '@/plugins/graphql/queries/v2/app-bundle.gql';
import SyncShopifyProducts from '@/plugins/graphql/mutations/v2/shopify/sync-shopify-products.gql';
import EcommerceProductCsvExport from '@/plugins/graphql/mutations/v2/ecommerce/product-export.gql';
import ButtonLoaderMixin from '@/helpers/vue_mixins/ButtonLoaderMixin';
import BaseOverlayLoader from '@/components/v2/BaseOverlayLoader';
import BaseSearchInput from '@/components/v2/BaseSearchInput';
import AlertMixin from '@/helpers/vue_mixins/AlertMixin';
import SearchMixin from '@/helpers/vue_mixins/v2/SearchMixin';
import GraphqlErrorMixin from '@/helpers/vue_mixins/GraphqlErrorMixin';
import EcommerceAppProducts from '@/plugins/graphql/queries/v2/ecommerce/products.gql';
import AppVisibleProductsCount from '@/plugins/graphql/queries/v2/ecommerce/app-visible-products-count.gql';
import AppVisibleCategoriesCount from '@/plugins/graphql/queries/v2/ecommerce/app-visible-categories-count.gql';
import appBasic from '@/plugins/graphql/queries/v2/app-basic.gql';
import { SHOPIFY, ECOMMERCE_B2C } from '@/helpers/constants/Bundles';
import moment from 'moment';
import ConfirmationDialog from '@/components/BaseConfirmDialog.vue';
import ActionButton from '@/components/action_button/Index.vue';
import ProductImport from '@/components/v2/ecommerce/ProductImport.vue';
import { ORANGE, PRIMARY } from '@/helpers/constants/Switch';
import OrangeThemeMixin from '@/helpers/vue_mixins/v2/OrangeThemeMixin';
import AppDetailsMixin from '@/helpers/vue_mixins/v2/ecommerce/AppDetailsMixin';
import AppShopifySetting from '@/plugins/graphql/queries/app-shopify-setting.gql';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
import Modal from '@/components/v3/Modal.vue';
let ProductViewManager = class ProductViewManager extends Mixins(ViewRendererMixin, RouterParamsAppMixin, ViewportSizeMixin, Mixins(ButtonLoaderMixin, OrangeThemeMixin, AppDetailsMixin), Mixins(AlertMixin, GraphqlErrorMixin, SearchMixin, CleverTapEventsMixin)) {
    constructor() {
        super(...arguments);
        this.loadingText = 'Exporting Products';
        this.toggleConfirm = false;
        this.shopifySetting = null;
    }
    get loadingTitle() {
        return this.loadingText;
    }
    get shopifyAppUuid() {
        return this.$route.params.appUUID || this.appUuid;
    }
    toggleConfirmation() {
        // if (!this.shopifySetting) {
        //   this.errorNotification('Syncing failed. Update your Shopify App Credentials.');
        //   return;
        // }
        // this.showConfirmationDialog();
        this.$refs.productListComponent.refetchProducts();
        this.toggleConfirm = true;
    }
    get productTemplate() {
        if (this.selectedProductsComponentLayoutOptions.name === 'grid') {
            return ProductListView;
        }
        return ProductGridView;
    }
    get isShopifyBundle() {
        return this.appBundle === SHOPIFY;
    }
    get isEcommerceBundle() {
        return this.appBundle === ECOMMERCE_B2C;
    }
    async exportProducts() {
        this.loadingText = 'Exporting Products';
        this.requestOnFlight();
        await this.$apollo
            .mutate({
            mutation: EcommerceProductCsvExport,
            variables: {
                input: {
                    appUuid: this.appUUID,
                },
            },
        })
            .then((data) => {
            const { products } = data.data.ecommerceProductCsvExport;
            if (products.edges.length > 0) {
                const emails = this.appEmailNotificationRecipientsList;
                this.successNotification(`Export will be sent to your emails: ${emails.join(', ')}`);
            }
            else {
                this.errorNotification('No Data Export');
            }
        })
            .catch((e) => {
            this.errorNotification(e.message);
            this.logError(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async syncProducts() {
        this.loadingText = 'Syncing Shopify Products';
        this.requestOnFlight();
        this.toggleConfirm = false;
        this.productAction('START PRODUCT SYNCING');
        await this.$apollo
            .mutate({
            mutation: SyncShopifyProducts,
            variables: {
                input: {
                    appUuid: this.appUUID,
                },
            },
            refetchQueries: [
                {
                    query: AppVisibleProductsCount,
                    variables: {
                        appUuid: this.appUUID,
                    },
                },
                {
                    query: EcommerceAppProducts,
                    variables: {
                        appUuid: this.appUUID,
                    },
                },
                {
                    query: AppVisibleCategoriesCount,
                    variables: {
                        appUuid: this.appUUID,
                    },
                },
                {
                    query: appBasic,
                    variables: {
                        appUuid: this.appUUID,
                    },
                },
            ],
        })
            .then(() => {
            this.updateProductSyncStatus(true);
            this.successNotification('Products syncing');
        })
            .catch((e) => {
            this.errorNotification(e.message);
            this.logError(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    lastSyncTextDetails(date) {
        return `Shopify Products are updated as of ${moment(date).format('MMMM DD, YYYY hh:mm a')}`;
    }
    get switchColor() {
        return this.orangeTheme ? ORANGE : PRIMARY;
    }
    startProductCreate() {
        this.productAction('START CREATE');
    }
    // This is for refetching products after syncing
    refetch() {
        if (this.shopifyProductSyncing.status === false) {
            this.$refs.productListComponent.refetchProducts();
            this.$refs.visibleProductsCount.refetchProductsCount();
            this.$refs.appBasicDetails.refetch();
        }
    }
    mounted() {
        const vm = this;
        if (vm.title) {
            const title = `${vm.title.toLowerCase()}_search_cursor`;
            const searchValue = JSON.parse(localStorage.getItem(title));
            vm.$refs.search.setValue(searchValue);
            this.search = searchValue;
        }
        this.viewProductsPage();
    }
};
__decorate([
    Inject()
], ProductViewManager.prototype, "shopifyProductSyncing", void 0);
__decorate([
    Inject()
], ProductViewManager.prototype, "updateProductSyncStatus", void 0);
__decorate([
    Prop({ type: String, default: null })
], ProductViewManager.prototype, "categoryUuid", void 0);
__decorate([
    Mutation('showConfirmationDialog')
], ProductViewManager.prototype, "showConfirmationDialog", void 0);
__decorate([
    Mutation('hideConfirmationDialog')
], ProductViewManager.prototype, "hideConfirmationDialog", void 0);
__decorate([
    Watch('shopifyProductSyncing.status')
], ProductViewManager.prototype, "refetch", null);
ProductViewManager = __decorate([
    Component({
        components: {
            FbShopActivationForm,
            VisibleProductsCount,
            AppBasicDetails,
            BaseOverlayLoader,
            ActionButton,
            BaseSearchInput,
            ProductImport,
            ConfirmationDialog,
            Modal,
        },
        apollo: {
            appBundle: {
                query: AppBundle,
                variables() {
                    return {
                        appUuid: this.appUUID,
                    };
                },
                update(data) {
                    return data.appServiceBundle.name;
                },
            },
            shopifySetting: {
                // Possible problem here: What if page is e-commerce?
                query: AppShopifySetting,
                variables() {
                    return { appUUID: this.shopifyAppUuid };
                },
                update(data) {
                    return data.app.shopifySetting;
                },
            },
        },
    })
], ProductViewManager);
export default ProductViewManager;
